import { getCoinDisplayName, wbtc_layer0_coinType } from './coinDisplayUtils';
import { getIcon } from './getIcon';
import { EXCLUDED_TOKENS } from './tokenConstants';
/**
 * Formats raw balance from blockchain units to human-readable decimal format
 * @param rawBalance - Balance in smallest token unit (e.g., MIST for SUI)
 * @param decimals - Number of decimal places for the token
 * @returns Formatted balance as string
 */
export const formatBalance = (rawBalance, decimals) => {
    const balance = parseFloat(rawBalance) / Math.pow(10, decimals);
    if (balance === 0)
        return '0';
    return balance.toString();
};
/**
 * Retrieves wallet balance for a given coin type with normalized coin type handling
 * Handles multiple coin type format variations (e.g., leading zeros, SUI format variations)
 * @param coinType - The coin type identifier to look up
 * @param decimals - Number of decimal places for the token
 * @param walletBalancesMap - Map of coin types to raw balances
 * @returns Formatted balance as string, '0' if not found
 */
export const getWalletBalance = (coinType, decimals, walletBalancesMap) => {
    if (!walletBalancesMap)
        return '0';
    // Try exact match first
    let balance = walletBalancesMap.get(coinType);
    if (balance)
        return formatBalance(balance, decimals);
    // For SUI, try both formats (::sui::SUI and 0x2::sui::SUI)
    if (coinType.includes('::sui::SUI')) {
        balance = walletBalancesMap.get('0x2::sui::SUI');
        if (balance)
            return formatBalance(balance, decimals);
    }
    // Try normalized format (0x0000...000002 -> 0x2)
    const normalizedType = coinType.replace(/^0x0+/, '0x');
    balance = walletBalancesMap.get(normalizedType);
    if (balance)
        return formatBalance(balance, decimals);
    return '0';
};
/**
 * Builds a list of tokens with prices and wallet balances from coin info data
 * Filters out excluded tokens and sorts by portfolio value (balance × price)
 * @param coinInfoData - Coin information data including prices and decimals
 * @param walletBalancesMap - Map of coin types to raw balances
 * @returns Array of Token objects sorted by portfolio value (descending)
 */
export const buildTokensList = (coinInfoData, walletBalancesMap) => {
    if (!coinInfoData)
        return [];
    const updatedTokenDetails = [];
    const coinInfoMap = new Map(Object.entries(coinInfoData));
    for (const [coinType, coinInfo] of coinInfoMap.entries()) {
        const token = {
            tokenId: coinType,
            symbol: getCoinDisplayName(coinInfo.symbol),
            icon: getIcon(coinType === wbtc_layer0_coinType
                ? 'wbtc_layer0'
                : coinInfo.symbol.toLowerCase(), coinType === wbtc_layer0_coinType ? 'wbtc_layer0' : coinInfo.symbol),
            price: coinInfo.price,
            walletBalance: getWalletBalance(coinType, parseInt(coinInfo.decimals), walletBalancesMap),
            decimals: parseInt(coinInfo.decimals),
        };
        updatedTokenDetails.push(token);
    }
    // Filter out excluded tokens
    const filteredTokens = updatedTokenDetails.filter((token) => !EXCLUDED_TOKENS.includes(token.symbol));
    // Sort by portfolio value (balance × price) descending
    const sortedTokens = filteredTokens.sort((a, b) => {
        return (parseFloat(b.walletBalance || '0') * parseFloat(b.price || '0') -
            parseFloat(a.walletBalance || '0') * parseFloat(a.price || '0'));
    });
    return sortedTokens;
};
